/**
 * The answers a parent already gave on the re-enrolment screen, ready to put back on it.
 *
 * None of this is checked against TASS or needs approving — it belongs to this re-enrolment. It
 * is restored only so a parent who goes back a step, or comes back tomorrow, does not have to
 * pick their discount and type their employee number again.
 */

const text = (value) => (value == null ? '' : String(value).trim());

const YES_NO = (value) => {
    const answer = text(value);
    if (/^y/i.test(answer)) return 'Yes';
    if (/^n/i.test(answer)) return 'No';
    return '';
};

/** Backend flags arrive as booleans or as Yes/No, depending on which save wrote them. */
const YES = (value) => value === true || value === 1 || YES_NO(value) === 'Yes';

/**
 * The card is already with the school.
 *
 * Three flags describe that, and they do not always agree. After a payment fails the pictures
 * can be uploaded and the attempt already submitted while validation is still false. Any one of
 * them means the parent has already sent the card; a false "validated" must not ask for it again.
 */
const cardAlreadySent = (saved) => YES(saved.hccImagesUploaded)
    || YES(saved.HealthcareCardUploaded)
    || YES(saved.HealthcareCardValidated)
    || YES(saved.HealthcareCardPreviouslySubmitted);

export const reenrolmentAnswersFromSaved = (saved) => {
    if (!saved) {
        return null;
    }

    const answers = {
        SM: YES_NO(saved.SM),
        EmployeeId: text(saved.EmployeeId),
        SelectedHCCValue: YES_NO(saved.SelectedHCCValue),
        HCCExpiryDate: text(saved.HCCExpiryDate),
        MC: YES_NO(saved.MC),
        SelectedDiscountValue: text(saved.SelectedDiscountValue),
        FTE: Number(saved.FTE) || 0,
        hccImagesUploaded: cardAlreadySent(saved),
        HealthcareCardUploaded: YES(saved.HealthcareCardUploaded) || YES(saved.hccImagesUploaded),
        HealthcareCardValidated: YES(saved.HealthcareCardValidated),
        HealthcareCardPreviouslySubmitted: YES(saved.HealthcareCardPreviouslySubmitted),
    };

    // Nothing answered yet is not worth applying — it would clear the page's own defaults.
    const answered = answers.SM || answers.SelectedHCCValue || answers.MC
        || answers.SelectedDiscountValue || answers.EmployeeId || answers.hccImagesUploaded;

    return answered ? answers : null;
};

/**
 * The Health Care Card expiry is stored the way the page writes it, "D-M-YYYY". Turning it back
 * into a date lets a returning parent keep the one they already picked.
 */
export const parseSavedHccExpiry = (value) => {
    const match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(String(value ?? '').trim());
    if (!match) return null;

    const [, day, month, year] = match.map(Number);
    const date = new Date(year, month - 1, day);

    // Reject something like 31-2-2029, which rolls over into March.
    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
        return null;
    }

    return date;
};

/** How the page writes it: "D-M-YYYY", or "No" when the parent has not picked one. */
export const formatHccExpiry = (value) => {
    const date = value?.$d ? new Date(value.$d) : (value instanceof Date ? value : null);
    if (!date || Number.isNaN(date.getTime())) return 'No';

    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};
